import React, { useEffect, useState } from 'react';
import { AddressDto, InvoiceMessageDto } from 'src/generated/services/TFinancialApi';
import { EditableTable, EditableTableColumn, EditableTableGroup } from 'src/presentations/components/organisms/editableTable/EditableTable';
import 'src/presentations/components/organisms/editableTable/EditableTable.css';
import { ValidateInvoiceStep } from 'src/presentations/components/molecules/uploadInvoice/types';
import { ValidateNode } from 'src/use-cases/internalDashboard/useValidation';

interface AddressesTableProps {
  invoiceMessage: InvoiceMessageDto;
  onUpdate: (addresses: InvoiceMessageDto) => void;
  validateJson: ValidateNode<InvoiceMessageDto>;
  validateUpdatedJson: ValidateNode<InvoiceMessageDto>;
  ocrMessage: InvoiceMessageDto;
  activeStep: ValidateInvoiceStep;
  isValidJson: boolean;
  setIsValidJson: (isInvalid: boolean) => void;
}

type AddressGroupKey = 'serviceProvider' | 'billedParty' | 'shipper' | 'consignee';

interface BaseGroupedAddress extends AddressDto {
  group: AddressGroupKey;
}
interface ServiceProviderAddress extends BaseGroupedAddress {
  group: 'serviceProvider';
}
interface BilledPartyAddress extends BaseGroupedAddress {
  group: 'billedParty';
}
interface ShipmentAddress extends BaseGroupedAddress {
  group: 'shipper' | 'consignee';
  shipmentNumber: string;
}
type GroupedAddress = ServiceProviderAddress | BilledPartyAddress | ShipmentAddress;

const ADDRESS_TYPE_LABELS: Record<AddressGroupKey, string> = {
  serviceProvider: 'Service Provider Addresses',
  billedParty: 'Billed Entity Addresses',
  shipper: 'Shipper Addresses',
  consignee: 'Consignee Addresses',
};

const updateInvoiceMessageWithAddresses = (
  invoiceMessage: InvoiceMessageDto,
  addressData: {
    serviceProviderAddresses?: AddressDto[];
    billedPartyAddresses?: AddressDto[];
    shipperAddresses?: Array<AddressDto & { shipmentNumber: string }>;
    consigneeAddresses?: Array<AddressDto & { shipmentNumber: string }>;
  }
): InvoiceMessageDto => {
  const updatedInvoiceMessage: InvoiceMessageDto = { ...invoiceMessage };
  updatedInvoiceMessage.serviceProviderAddresses = addressData.serviceProviderAddresses || [];
  updatedInvoiceMessage.billedPartyAddresses = addressData.billedPartyAddresses || [];
  if (Array.isArray(updatedInvoiceMessage.shipments)) {
    const isContainBlankShipmentNumber = invoiceMessage.shipments.some((s) => s.shipmentNumber === '');
    // shipper
    if (addressData.shipperAddresses) {
      const shipperMap = addressData.shipperAddresses.reduce((acc: Record<string, AddressDto[]>, addr) => {
        if (!addr.shipmentNumber && addr.shipmentNumber !== '' && !isContainBlankShipmentNumber) return acc;
        if (!acc[addr.shipmentNumber]) acc[addr.shipmentNumber] = [];
        const { shipmentNumber, ...address } = addr;
        // Only push if at least 1 field of address has value and not blank
        const hasValue = Object.keys(address).some((k) => typeof address[k] === 'string' && address[k].trim() !== '');
        if (hasValue) {
          acc[addr.shipmentNumber].push(address);
        }
        return acc;
      }, {});
      updatedInvoiceMessage.shipments = updatedInvoiceMessage.shipments.map((s) => ({
        ...s,
        shipperAddresses: shipperMap[s.shipmentNumber] || [],
      }));
    }
    // consignee
    if (addressData.consigneeAddresses) {
      const consigneeMap = addressData.consigneeAddresses.reduce((acc: Record<string, AddressDto[]>, addr) => {
        if (!addr.shipmentNumber && addr.shipmentNumber !== '' && !isContainBlankShipmentNumber) return acc;
        if (!acc[addr.shipmentNumber]) acc[addr.shipmentNumber] = [];
        const { shipmentNumber, ...address } = addr;
        // Only push if at least 1 field of address has value and not blank
        const hasValue = Object.keys(address).some((k) => typeof address[k] === 'string' && address[k].trim() !== '');
        if (hasValue) {
          acc[addr.shipmentNumber].push(address);
        }
        return acc;
      }, {});
      updatedInvoiceMessage.shipments = updatedInvoiceMessage.shipments.map((s) => ({
        ...s,
        consigneeAddresses: consigneeMap[s.shipmentNumber] || [],
      }));
    }
  }
  return updatedInvoiceMessage;
};

export const AddressesTable = ({
  invoiceMessage,
  onUpdate,
  validateJson,
  validateUpdatedJson,
  ocrMessage,
  activeStep,
  isValidJson,
  setIsValidJson,
}: AddressesTableProps) => {
  const isContainBlankShipmentNumber = invoiceMessage.shipments.some((s) => s.shipmentNumber === '');
  const [groupedAddresses, setGroupedAddresses] = useState<Record<AddressGroupKey, GroupedAddress[]>>({
    serviceProvider: [],
    billedParty: [],
    shipper: [],
    consignee: [],
  });

  // Sync groupedAddresses from invoiceMessage when invoiceMessage changes
  React.useEffect(() => {
    const groups: Record<AddressGroupKey, GroupedAddress[]> = {
      serviceProvider: [],
      billedParty: [],
      shipper: [],
      consignee: [],
    };
    if (invoiceMessage.serviceProviderAddresses) {
      groups.serviceProvider = invoiceMessage.serviceProviderAddresses.map((addr) => ({ ...addr, group: 'serviceProvider' }));
    }
    if (invoiceMessage.billedPartyAddresses) {
      groups.billedParty = invoiceMessage.billedPartyAddresses.map((addr) => ({ ...addr, group: 'billedParty' }));
    }
    if (invoiceMessage.shipments) {
      invoiceMessage.shipments.forEach((shipment) => {
        if (shipment.shipmentNumber || shipment.shipmentNumber === '') {
          if (shipment.shipperAddresses) {
            if (shipment.shipperAddresses.length === 0) {
              groups.shipper.push({ address: '', city: '', zipcode: '', country: '', group: 'shipper', shipmentNumber: shipment.shipmentNumber });
            } else {
              groups.shipper.push(
                ...shipment.shipperAddresses.map((addr) => ({
                  ...addr,
                  group: 'shipper' as const,
                  shipmentNumber: shipment.shipmentNumber,
                }))
              );
            }
          }
          if (shipment.consigneeAddresses) {
            if (shipment.consigneeAddresses.length === 0) {
              groups.consignee.push({
                address: '',
                city: '',
                zipcode: '',
                country: '',
                group: 'consignee',
                shipmentNumber: shipment.shipmentNumber,
              });
            } else {
              groups.consignee.push(
                ...shipment.consigneeAddresses.map((addr) => ({
                  ...addr,
                  group: 'consignee' as const,
                  shipmentNumber: shipment.shipmentNumber,
                }))
              );
            }
          }
        }
      });
    }
    setGroupedAddresses(groups);
  }, [invoiceMessage]);

  const [expanded, setExpanded] = useState<Record<AddressGroupKey, boolean>>({
    serviceProvider: true,
    billedParty: true,
    shipper: true,
    consignee: true,
  });

  const handleToggle = (key: AddressGroupKey) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAdd = (key: AddressGroupKey, shipmentNumber?: string) => {
    const baseAddress = { address: '', city: '', zipcode: '', country: '', group: key };
    const newAddress: GroupedAddress =
      key === 'shipper' || key === 'consignee'
        ? ({ ...baseAddress, group: key, shipmentNumber: shipmentNumber ?? '' } as ShipmentAddress)
        : ({ ...baseAddress, group: key } as ServiceProviderAddress | BilledPartyAddress);
    const updated = { ...groupedAddresses };
    updated[key] = [...updated[key], newAddress];
    setGroupedAddresses(updated);
    if ((key === 'shipper' || key === 'consignee') && !isContainBlankShipmentNumber) {
      setIsValidJson(false);
    }
  };

  const handleAddressesUpdate = (groups: Record<AddressGroupKey, GroupedAddress[]>) => {
    const addressData = {
      serviceProviderAddresses: groups.serviceProvider.map(({ group, ...rest }) => rest),
      billedPartyAddresses: groups.billedParty.map(({ group, ...rest }) => rest),
      shipperAddresses: (groups.shipper as ShipmentAddress[]).map(({ group, shipmentNumber, ...rest }) => ({ ...rest, shipmentNumber })),
      consigneeAddresses: (groups.consignee as ShipmentAddress[]).map(({ group, shipmentNumber, ...rest }) => ({ ...rest, shipmentNumber })),
    };
    const updatedInvoiceMessage = updateInvoiceMessageWithAddresses(invoiceMessage, addressData);
    if (isValidJson) {
      onUpdate(updatedInvoiceMessage);
    }
  };

  const handleDelete = (key: AddressGroupKey, idx: number) => {
    const updated = { ...groupedAddresses };
    updated[key] = updated[key].map((addr, i) => {
      if (i !== idx) return addr;
      // Set all fields to blank except shipmentNumber
      const blanked: any = { ...addr };
      Object.keys(blanked).forEach((field) => {
        if (field !== 'shipmentNumber' && field !== 'group') {
          blanked[field] = '';
        }
      });
      if ('shipmentNumber' in addr) {
        blanked.shipmentNumber = addr.shipmentNumber;
      }
      blanked.group = addr.group;
      return blanked;
    });
    setGroupedAddresses(updated);
    handleAddressesUpdate(updated);
  };

  // Helper to get error and helperText for a field in a row (like ChargesTable)
  const getFieldValidation = (v: any, updatedV: any, originalValue: any) => ({
    error: !!v && v.isValid === false,
    helperText: v?.exception?.error ?? '',
    updatedError: !!updatedV && updatedV.isValid === false,
    updatedHelperText: updatedV?.exception?.error ?? '',
    originalValue,
  });

  const getShipmentValidation = (groupKey: AddressGroupKey, rowIdx: number, field: string) => {
    const row = groupedAddresses[groupKey][rowIdx] as ShipmentAddress;
    const shipmentIdx = invoiceMessage.shipments?.findIndex((s) => s.shipmentNumber === row.shipmentNumber) ?? -1;
    if (shipmentIdx === -1) return { error: false, helperText: '', updatedError: false, updatedHelperText: '', originalValue: undefined };
    const shipment = invoiceMessage.shipments?.[shipmentIdx];
    const addrList = groupKey === 'shipper' ? shipment?.shipperAddresses : shipment?.consigneeAddresses;
    let addressIdx = -1;
    if (addrList) {
      addressIdx = addrList.findIndex((a) => a.address === row.address && a.city === row.city && a.zipcode === row.zipcode && a.country === row.country);
    }
    if (addressIdx === -1) return { error: false, helperText: '', updatedError: false, updatedHelperText: '', originalValue: undefined };
    const v = (validateJson.shipments as any)?.value?.[shipmentIdx]?.[groupKey === 'shipper' ? 'shipperAddresses' : 'consigneeAddresses']?.value?.[addressIdx]
      ?.value?.[field];
    const updatedV = (validateUpdatedJson?.shipments as any)?.value?.[shipmentIdx]?.[groupKey === 'shipper' ? 'shipperAddresses' : 'consigneeAddresses']
      ?.value?.[addressIdx]?.value?.[field];
    let originalValue;
    if (ocrMessage?.shipments) {
      const ocrShipment = ocrMessage.shipments?.[shipmentIdx];
      const ocrAddrList = groupKey === 'shipper' ? ocrShipment?.shipperAddresses : ocrShipment?.consigneeAddresses;
      originalValue = ocrAddrList?.[addressIdx]?.[field];
    }
    return getFieldValidation(v, updatedV, originalValue);
  };

  const getAddressValidation = (groupKey: AddressGroupKey, rowIdx: number, field: string) => {
    if (!validateJson) return { error: false, helperText: '', updatedError: false, updatedHelperText: '', originalValue: undefined };
    if (groupKey === 'serviceProvider') {
      const v = (validateJson.serviceProviderAddresses as any)?.value?.[rowIdx]?.value?.[field];
      const updatedV = (validateUpdatedJson?.serviceProviderAddresses as any)?.value?.[rowIdx]?.value?.[field];
      const originalValue = ocrMessage?.serviceProviderAddresses?.[rowIdx]?.[field];
      return getFieldValidation(v, updatedV, originalValue);
    }
    if (groupKey === 'billedParty') {
      const v = (validateJson.billedPartyAddresses as any)?.value?.[rowIdx]?.value?.[field];
      const updatedV = (validateUpdatedJson?.billedPartyAddresses as any)?.value?.[rowIdx]?.value?.[field];
      const originalValue = ocrMessage?.billedPartyAddresses?.[rowIdx]?.[field];
      return getFieldValidation(v, updatedV, originalValue);
    }
    if (groupKey === 'shipper' || groupKey === 'consignee') {
      return getShipmentValidation(groupKey, rowIdx, field);
    }
    return { error: false, helperText: '', updatedError: false, updatedHelperText: '', originalValue: undefined };
  };

  // Only update local state on cell edit, do not call handleAddressesUpdate here to avoid infinite loop
  const handleCellEdit = (key: AddressGroupKey, idx: number, field: string, value: string) => {
    setGroupedAddresses((prev) => ({ ...prev, [key]: prev[key].map((addr, i) => (i === idx ? { ...addr, [field]: value } : addr)) }));
  };

  const handleCellBlur = (key: AddressGroupKey, idx: number, field: string, value: string) => {
    handleCellEdit(key, idx, field, value);
    if (isValidJson) {
      handleAddressesUpdate(groupedAddresses);
    }
  };

  const checkIsValidShipmentNumber = (): boolean => {
    let isValid = true;
    groupedAddresses.shipper.forEach((row) => {
      if (checkShipmentNumberExists((row as any)?.shipmentNumber).error) {
        isValid = false;
      }
    });
    groupedAddresses.consignee.forEach((row) => {
      if (checkShipmentNumberExists((row as any)?.shipmentNumber).error) {
        isValid = false;
      }
    });
    setIsValidJson(isValid);
    return isValid;
  };

  useEffect(() => {
    checkIsValidShipmentNumber();
  }, [groupedAddresses]);

  const checkShipmentNumberExists = (shipmentNumber: string) => {
    const shipmentExists = invoiceMessage.shipments?.some((s) => s.shipmentNumber === shipmentNumber);
    if (!shipmentExists) {
      return {
        error: true,
        helperText: 'Shipment number does not exist in shipments data.',
      };
    }
    return {
      error: false,
      helperText: '',
    };
  };

  // Add error and helperText to columns using validateJson
  const columns: EditableTableColumn[] = [
    {
      key: 'shipmentNumber',
      label: 'Shipment Number',
      width: 200,
      isEditable: false,
    },
    {
      key: 'address',
      label: 'Address',
      width: 400,
    },
    {
      key: 'city',
      label: 'City',
      width: 200,
    },
    {
      key: 'zipcode',
      label: 'ZipCode',
      width: 150,
    },
    {
      key: 'country',
      label: 'Country',
      width: 150,
    },
  ];

  // Use EditableTable for structured mode
  const groups: EditableTableGroup[] = (['serviceProvider', 'billedParty', 'consignee', 'shipper'] as AddressGroupKey[]).map((key) => ({
    key,
    label: `${ADDRESS_TYPE_LABELS[key]} (${groupedAddresses[key].length} addresses)`,
    rows: groupedAddresses[key].map((row, idx) => {
      const rowWithValidation = { ...row };
      columns.forEach((col) => {
        const v = getAddressValidation(key, idx, col.key);
        rowWithValidation[`${col.key}__error`] = v.error;
        rowWithValidation[`${col.key}__helperText`] = v.helperText;
        rowWithValidation[`${col.key}__updatedError`] = v.updatedError;
        rowWithValidation[`${col.key}__originalValue`] = v.originalValue;
        if (col.key === 'shipmentNumber' && (key === 'shipper' || key === 'consignee')) {
          const { error, helperText } = checkShipmentNumberExists((rowWithValidation as any).shipmentNumber);
          if (error) {
            rowWithValidation[`${col.key}__error`] = error;
            rowWithValidation[`${col.key}__helperText`] = helperText;
          }
        }
      });
      return rowWithValidation;
    }),
    expanded: expanded[key],
    onToggle: () => handleToggle(key),
    onAdd: () => handleAdd(key),
    isHideAddButton: key === 'shipper' || key === 'consignee',
  }));

  return (
    <EditableTable
      columns={columns}
      groups={groups}
      onDelete={(groupKey, rowIdx) => handleDelete(groupKey as AddressGroupKey, rowIdx)}
      onCellEdit={(groupKey, rowIdx, field, value) => handleCellEdit(groupKey as AddressGroupKey, rowIdx, field, value)}
      onBlur={(groupKey, rowIdx, field, value) => handleCellBlur(groupKey as AddressGroupKey, rowIdx, field, value)}
      activeStep={activeStep}
    />
  );
};
